import { type KeyboardEvent, type PointerEvent, useLayoutEffect, useRef, useState } from "react"

import { comoFecha } from "@/lib/fechas"

import { comoTasa, diaYMes } from "./formato"
import type { PuntoTasa } from "./tipos"

const ALTO = 220
const MARGEN = { arriba: 16, derecha: 76, abajo: 28, izquierda: 64 }

/** Días desde 1970 de un `YYYY-MM-DD`, sin pasar por la hora local (ver `lib/fechas`). */
function dia(fecha: string): number {
  const [a = 1970, m = 1, d = 1] = fecha.slice(0, 10).split("-").map(Number)
  return Date.UTC(a, m - 1, d) / 86_400_000
}

/** Un paso redondo —1, 2, 2,5 o 5 por potencia de diez— para las marcas del eje. */
function pasoRedondo(bruto: number): number {
  const potencia = 10 ** Math.floor(Math.log10(bruto))
  const r = bruto / potencia
  const base = r <= 1 ? 1 : r <= 2 ? 2 : r <= 2.5 ? 2.5 : r <= 5 ? 5 : 10
  return base * potencia
}

/** Tres o cuatro marcas redondas que abarcan los valores. */
function marcasDe(minimo: number, maximo: number): number[] {
  let bajo = minimo
  let alto = maximo
  // Una serie plana —el dólar que el BCV no movió— no tiene rango: se le da un 1 %.
  if (alto - bajo < 1e-9) {
    const holgura = Math.max(Math.abs(alto) * 0.01, 0.01)
    bajo -= holgura
    alto += holgura
  }
  const paso = pasoRedondo((alto - bajo) / 3)
  const desde = Math.floor(bajo / paso) * paso
  const hasta = Math.ceil(alto / paso) * paso
  const marcas: number[] = []
  for (let v = desde; v <= hasta + paso / 2; v += paso) marcas.push(Number(v.toFixed(6)))
  return marcas
}

/**
 * La evolución de una divisa en bolívares, al cierre de cada día.
 *
 * ⚠ **Dibujado a mano en SVG y no con una librería**, como hace `constructora_v2`: es
 * una línea con su eje, y una dependencia de cientos de kilobytes para eso pesaría más
 * que la pantalla entera. El color es el vino de marca (`--primary`) y no su tinta
 * clara: validado contra la superficie oscura con la guía de visualización, la tinta
 * clara no llega al croma mínimo y se lee casi gris. Línea de 2 px en el acento,
 * un lavado al 10 % debajo, rejilla de un píxel sólida y recesiva, y el valor escrito
 * al final de la línea, que es la cifra que se viene a ver.
 *
 * ⚠ **El eje X es de FECHAS, no de puntos.** El BCV no publica los fines de semana; con
 * los puntos repartidos a partes iguales, el viernes y el lunes quedarían pegados como
 * si fueran días seguidos, y la pendiente mentiría.
 *
 * ⚠ **El eje Y no empieza en cero.** Es una tendencia, no una magnitud: con el cero, un
 * movimiento del 2 % sería una línea plana. Las marcas son redondas y están escritas.
 *
 * ⚠ **La guía vertical sigue al puntero y se engancha al día más cercano**: nadie apunta
 * a una línea de dos píxeles. Con el teclado, las flechas recorren los días y el día
 * elegido se anuncia. Y la tabla de debajo es su gemela accesible: ningún valor se lee
 * sólo aquí.
 */
export function GraficoTasa({ puntos, moneda }: { puntos: readonly PuntoTasa[]; moneda: string }) {
  const caja = useRef<HTMLDivElement | null>(null)
  const [ancho, setAncho] = useState(640)
  const [activo, setActivo] = useState<number | null>(null)

  useLayoutEffect(() => {
    const el = caja.current
    if (el === null) return
    // Se dibuja a la medida en píxeles, no estirando un `viewBox`: estirado, el texto
    // de los ejes se deformaría con el ancho de la tarjeta.
    const medir = () => {
      if (el.clientWidth > 0) setAncho(el.clientWidth)
    }
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(el)
    return () => observador.disconnect()
  }, [])

  if (puntos.length === 0) return null

  const valores = puntos.map((p) => Number(p.bs_por_unidad))
  const dias = puntos.map((p) => dia(p.fecha_valor))
  const marcas = marcasDe(Math.min(...valores), Math.max(...valores))
  const yMin = marcas[0] ?? 0
  const yMax = marcas.at(-1) ?? 1
  const d0 = dias[0] ?? 0
  const dN = dias.at(-1) ?? 0
  const anchoPlot = Math.max(ancho - MARGEN.izquierda - MARGEN.derecha, 40)
  const altoPlot = ALTO - MARGEN.arriba - MARGEN.abajo
  const base = MARGEN.arriba + altoPlot
  const ultimo = puntos.length - 1

  const x = (i: number) =>
    MARGEN.izquierda +
    (dN === d0 ? anchoPlot / 2 : (((dias[i] ?? d0) - d0) / (dN - d0)) * anchoPlot)
  const y = (v: number) => MARGEN.arriba + (1 - (v - yMin) / (yMax - yMin)) * altoPlot

  const linea = valores
    .map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join("")
  const area = `${linea}L${x(ultimo).toFixed(1)},${base}L${x(0).toFixed(1)},${base}Z`

  // Las fechas del eje: la primera, la última y las que quepan en medio sin pisarse.
  const cuantas = Math.min(puntos.length, Math.max(2, Math.floor(anchoPlot / 90)))
  const indicesX = [
    ...new Set(
      Array.from({ length: cuantas }, (_, k) =>
        Math.round((k * ultimo) / Math.max(cuantas - 1, 1)),
      ),
    ),
  ]

  function engancharAlPuntero(e: PointerEvent<SVGSVGElement>) {
    const px = e.clientX - e.currentTarget.getBoundingClientRect().left
    let mejor = 0
    for (let k = 1; k < puntos.length; k++) {
      if (Math.abs(x(k) - px) < Math.abs(x(mejor) - px)) mejor = k
    }
    setActivo(mejor)
  }

  function teclas(e: KeyboardEvent<HTMLDivElement>) {
    const destino: Record<string, (a: number) => number> = {
      ArrowRight: (a) => Math.min(a + 1, ultimo),
      ArrowLeft: (a) => Math.max(a - 1, 0),
      Home: () => 0,
      End: () => ultimo,
    }
    const mover = destino[e.key]
    if (mover === undefined) return
    e.preventDefault()
    setActivo((a) => mover(a ?? ultimo))
  }

  const primero = puntos[0]
  const final = puntos[ultimo]
  const punto = activo === null ? null : (puntos[activo] ?? null)
  const vUltimo = valores[ultimo] ?? 0

  return (
    <div ref={caja} className="relative">
      {/* Una `<figure>` y no un `div` con rol: un gráfico es exactamente eso, y el
          elemento ya trae su rol. */}
      <figure
        // biome-ignore lint/a11y/noNoninteractiveTabindex: se recorre con las flechas; sin foco no habría forma de hacerlo con el teclado.
        tabIndex={0}
        aria-roledescription="gráfico"
        aria-label={
          primero && final
            ? `Bolívares por 1 ${moneda}, del ${comoFecha(primero.fecha_valor)} al ${comoFecha(final.fecha_valor)}: de ${comoTasa(primero.bs_por_unidad)} a ${comoTasa(final.bs_por_unidad)}. Las flechas recorren los días.`
            : `Bolívares por 1 ${moneda}`
        }
        onKeyDown={teclas}
        onFocus={() => setActivo((a) => a ?? ultimo)}
        onBlur={() => setActivo(null)}
        className="foco-cristal rounded-[var(--radius-control)]"
      >
        <svg
          aria-hidden={true}
          width={ancho}
          height={ALTO}
          viewBox={`0 0 ${ancho} ${ALTO}`}
          className="block touch-pan-y"
          onPointerMove={engancharAlPuntero}
          onPointerLeave={() => setActivo(null)}
        >
          {marcas.map((m) => (
            <g key={m}>
              <line
                x1={MARGEN.izquierda}
                x2={MARGEN.izquierda + anchoPlot}
                y1={y(m)}
                y2={y(m)}
                stroke="var(--border)"
                strokeWidth={1}
              />
              <text
                x={MARGEN.izquierda - 8}
                y={y(m)}
                dy="0.32em"
                textAnchor="end"
                fontSize={11}
                fill="var(--fg-muted)"
                className="tabular-nums"
              >
                {comoTasa(m.toFixed(2))}
              </text>
            </g>
          ))}
          {indicesX.map((k) => (
            <text
              key={k}
              x={x(k)}
              y={ALTO - 8}
              textAnchor="middle"
              fontSize={11}
              fill="var(--fg-muted)"
              className="tabular-nums"
            >
              {diaYMes(puntos[k]?.fecha_valor ?? "")}
            </text>
          ))}

          <path d={area} fill="var(--primary)" fillOpacity={0.1} stroke="none" />
          <path
            d={linea}
            fill="none"
            stroke="var(--primary)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {activo !== null && (
            <line
              x1={x(activo)}
              x2={x(activo)}
              y1={MARGEN.arriba}
              y2={base}
              stroke="var(--fg-muted)"
              strokeOpacity={0.6}
              strokeWidth={1}
            />
          )}
          {activo !== null && activo !== ultimo && (
            <circle
              cx={x(activo)}
              cy={y(valores[activo] ?? 0)}
              r={4}
              fill="var(--primary)"
              stroke="var(--surface)"
              strokeWidth={2}
            />
          )}
          <circle
            cx={x(ultimo)}
            cy={y(vUltimo)}
            r={4}
            fill="var(--primary)"
            stroke="var(--surface)"
            strokeWidth={2}
          />
          <text
            x={x(ultimo) + 10}
            y={y(vUltimo)}
            dy="0.32em"
            fontSize={12}
            fontWeight={600}
            fill="var(--fg)"
            className="tabular-nums"
          >
            {comoTasa(final?.bs_por_unidad ?? "0")}
          </text>
        </svg>
      </figure>

      {punto !== null && activo !== null && (
        <div
          aria-hidden={true}
          className="pointer-events-none absolute z-10 rounded-[var(--radius-control)] border px-2.5 py-1.5 text-xs"
          style={{
            left: Math.min(Math.max(x(activo) - 64, 0), Math.max(ancho - 128, 0)),
            top: Math.max(y(valores[activo] ?? 0) - 56, 0),
            backgroundColor: "var(--surface)",
            borderColor: "var(--border)",
            boxShadow: "var(--elev-2)",
          }}
        >
          <strong className="block text-sm tabular-nums">{comoTasa(punto.bs_por_unidad)} Bs</strong>
          <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
            {comoFecha(punto.fecha_valor)}
          </span>
        </div>
      )}
      <p className="sr-only" aria-live="polite">
        {punto ? `${comoFecha(punto.fecha_valor)}: ${comoTasa(punto.bs_por_unidad)} bolívares` : ""}
      </p>
    </div>
  )
}
